import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AdminGate } from "../../components/admin/AdminGate";
import { RequestHistoryTable } from "../../components/requests/RequestHistoryTable";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import styles from "../../components/admin/Admin.module.css";
import { fetchAllPeople, type PersonSummary } from "../../api/entitlementAdmin";
import { fetchPersonRequests, type PersonRequestHistoryItem } from "../../api/requestsAdmin";
import { PersonActionsSection } from "./EntitlementAdminPage";
import { PersonActivityPanel } from "../../components/admin/PersonActivityPanel";

const AdministerPermission = "entitlement.administer";

/** Full-page editor for one user: the edit controls followed by the user's request history. */
export function UserDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { personId = "" } = useParams();

  return (
    <div className={styles.usersPage}>
      <AdminGate permissions={[AdministerPermission]} title={t("admin.entitlement.denied.title")} description={t("admin.entitlement.denied.description")}>
        <UserDetailContent personId={personId} />
      </AdminGate>
    </div>
  );
}

function UserDetailContent({ personId }: { personId: string }): React.JSX.Element {
  const { t } = useTranslation();
  const [person, setPerson] = useState<PersonSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyVersion, setHistoryVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPerson(null);
    setError(null);
    void (async () => {
      const result = await fetchAllPeople();
      if (cancelled) {
        return;
      }

      if (result.kind === "ok") {
        const match = result.response.items.find((item) => item.id === personId);
        if (match) {
          setPerson(match);
        } else {
          setError("User not found.");
        }
      } else {
        setError(result.kind === "denied" ? t("admin.entitlement.denied.description") : result.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId, t]);

  return (
    <section className={styles.usersSection}>
      {error && <ErrorState description={error} />}
      {!error && person === null && <LoadingState label={t("admin.entitlement.person.associationsLoading")} />}
      {person !== null && (
        <>
          <PersonActionsSection person={person} onDisplayNameSaved={(displayName) => setPerson({ ...person, displayName })} onAllocationSaved={() => setHistoryVersion((current) => current + 1)} />
          <PersonActivityPanel personId={person.id} />
          <RequestHistory personId={person.id} version={historyVersion} />
        </>
      )}
    </section>
  );
}

function RequestHistory({ personId, version }: { personId: string; version: number }): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; items: PersonRequestHistoryItem[] } | { kind: "error"; message: string }>({ kind: "loading" });
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const visible = useMemo(() => (state.kind === "loaded" && Array.isArray(state.items) ? state.items.filter((item) => showWithdrawn || item.status !== "Withdrawn") : []), [state, showWithdrawn]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const result = await fetchPersonRequests(personId);
      if (cancelled) {
        return;
      }

      if (result.kind === "ok") {
        setState({ kind: "loaded", items: result.response });
      } else {
        setState({ kind: "error", message: result.kind === "denied" ? "You don't have permission to view request history." : result.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId, version]);

  return (
    <section className={styles.requestHistory}>
      {state.kind === "loading" && <LoadingState label="Loading requests…" />}
      {state.kind === "error" && <ErrorState description={state.message} />}
      {state.kind === "loaded" && state.items.length === 0 && <EmptyState title="No requests" description="This user has not raised any requests." />}
      {state.kind === "loaded" && state.items.length > 0 && (
        <>
          <div className={`${styles.tableToolbar} ${styles.requestHistoryToolbar}`}>
            <label className={styles.toolbarToggle}>
              {t("admin.requests.showWithdrawn", "Show Withdrawn Requests")}
              <input type="checkbox" className={styles.allocationToggle} checked={showWithdrawn} onChange={(event) => setShowWithdrawn(event.target.checked)} />
            </label>
          </div>
          <RequestHistoryTable items={visible} />
        </>
      )}
    </section>
  );
}
